// Shared i18n initialization for admin-web and mirror-app.
//
// Both apps import { initI18n, SUPPORTED_LANGS, useI18n } from "@faceheart/i18n"
// and call initI18n() once at startup. Translations live in src/locales/*.json
// and are bundled by the consuming app's vite build.
//
// Namespacing:
//   - `common` — shared widgets (buttons, validation messages)
//   - `admin`  — admin-web only
//   - `mirror` — mirror-app only
//
// Apps default to loading just the namespaces they need (saves bundle), but
// the locale JSON files include everything so we don't fragment translation
// management.

import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next, useTranslation } from "react-i18next";

import zhTW from "./locales/zh-TW.json";
import en from "./locales/en.json";

export const SUPPORTED_LANGS = [
  { code: "zh-TW", label: "繁體中文" },
  { code: "en", label: "English" },
] as const;

export type LangCode = (typeof SUPPORTED_LANGS)[number]["code"];

export type Namespace = "common" | "admin" | "mirror";

// localStorage key used by the language-detector plugin AND by the
// LanguageSwitcher to write directly. Centralised here so the two stay
// in sync.
export const LANG_STORAGE_KEY = "faceheart.lang";

let initialized = false;

export interface InitOpts {
  // Which namespaces to load. Apps pass ["common", "admin"] or
  // ["common", "mirror"]; the package itself doesn't need to know
  // which app is calling.
  ns: Namespace[];
  // Override the fallback language. Defaults to zh-TW because the
  // initial deployment is for a Taiwanese customer.
  fallbackLng?: LangCode;
}

export function initI18n(opts: InitOpts): typeof i18n {
  if (initialized) return i18n;
  initialized = true;

  void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      // Bundle resources at init time — small payload, no async load,
      // no flash of untranslated UI.
      resources: {
        "zh-TW": zhTW,
        en: en,
      },
      fallbackLng: opts.fallbackLng ?? "zh-TW",
      // Only require the namespaces the caller asked for; missing keys
      // fall back to the key string itself (visible at-a-glance during
      // dev).
      ns: opts.ns,
      defaultNS: opts.ns[0],
      // The plugin order tries each in turn until one returns a value:
      //   1. user choice from our localStorage key
      //   2. browser's navigator.language
      //   3. fallbackLng
      detection: {
        order: ["localStorage", "navigator"],
        lookupLocalStorage: LANG_STORAGE_KEY,
        caches: ["localStorage"],
      },
      interpolation: {
        // React already escapes — disabling i18next's escaping prevents
        // double-encoding of "&" etc. in interpolated values.
        escapeValue: false,
      },
      // Don't kill the page when a translation is missing; just log + use key.
      returnEmptyString: false,
    });

  return i18n;
}

// Convenience re-export so callers don't need both packages on their
// import list. Use `useI18n("admin")` to scope to the admin namespace.
export function useI18n(ns?: Namespace) {
  return useTranslation(ns);
}

export { i18n };
export { LanguageSwitcher } from "./LanguageSwitcher";
// Re-export TFunction so consumers don't need a direct i18next dep just to
// type a t() parameter in a helper function.
export type { TFunction } from "i18next";
